/**
 * @component group-shell
 * @css group-shell.css
 * @primitives toolbar-action-bar, toolbar-overflow-actions, post-writer, posts-feed
 */
import ProtectedComponent from './protected-component.js';
import logger from '../utils/logger.js';
import { teardownGroupContextNav, syncGroupContextNav } from '../utils/group-context-nav.js';
import { getProxyUrl } from '../utils/image-proxy-processor.js';
import { THREAD_ICON } from '../utils/group-threads.js';
import { canInviteMembers, canCreateGroupEvents } from '../utils/group-invite-policy.js';

const MOD_ROLES = ['owner', 'admin', 'moderator'];
const ADMIN_BRANDING_ROLES = ['owner', 'admin'];

class GroupShell extends ProtectedComponent {
  static get observedAttributes() {
    return ['group-id', 'thread-id', 'initial-page'];
  }

  constructor() {
    super();
    this.groupId = null;
    this.threadId = null;
    this.group = null;
    this.threads = [];
    this.error = null;
  }

  attributeChangedCallback() {
    if (!this.isConnected) {
      return;
    }
    this.groupId = parseInt(this.getAttribute('group-id'), 10);
    const tid = this.getAttribute('thread-id');
    this.threadId = tid && tid !== '' ? parseInt(tid, 10) : null;
    if (this.currentUser && !Number.isNaN(this.groupId)) {
      this.refresh();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.groupId = parseInt(this.getAttribute('group-id'), 10);
    const tid = this.getAttribute('thread-id');
    this.threadId = tid && tid !== '' ? parseInt(tid, 10) : null;
    this.addEventListener('group-threads-changed', () => this.refreshThreads());
    this.addEventListener('group-settings-saved', () => this.refresh());
    this.refresh();
  }

  disconnectedCallback() {
    teardownGroupContextNav();
    super.disconnectedCallback();
  }

  setCurrentUser(user) {
    super.setCurrentUser(user);
    if (user && !Number.isNaN(this.groupId)) {
      this.refresh();
    }
  }

  isMod() {
    const r = this.group?.membership?.role;
    return r && MOD_ROLES.includes(r);
  }

  canBrand() {
    const r = this.group?.membership?.role;
    return r && ADMIN_BRANDING_ROLES.includes(r);
  }

  activeThreads() {
    return this.threads.filter((t) => !t.archived_at);
  }

  async refreshThreads() {
    if (!window.API || !this.currentUser || Number.isNaN(this.groupId)) {
      return;
    }
    try {
      const threadsUrl = this.isMod()
        ? `/groups/${this.groupId}/threads?include_archived=true`
        : `/groups/${this.groupId}/threads`;
      const threadsData = await window.API.request(threadsUrl);
      this.threads = threadsData.threads || [];

      const roleLabel = this.group.membership?.role
        ? `You: ${this.group.membership.role}`
        : null;
      syncGroupContextNav({
        groupId: this.groupId,
        groupName: this.group.name,
        roleLabel,
        threads: this.activeThreads()
      });

      const writer = this.querySelector('post-writer');
      if (writer && typeof writer.setGroupThreads === 'function') {
        writer.setGroupThreads(this.activeThreads());
      }
    } catch (e) {
      logger.warn('GroupShell refreshThreads failed', e);
    }
  }

  async refresh() {
    if (!window.API || !this.currentUser || Number.isNaN(this.groupId)) {
      return;
    }
    this.error = null;
    try {
      this.group = await window.API.request(`/groups/${this.groupId}`);
      const threadsUrl = this.isMod()
        ? `/groups/${this.groupId}/threads?include_archived=true`
        : `/groups/${this.groupId}/threads`;
      const threadsData = await window.API.request(threadsUrl);
      this.threads = threadsData.threads || [];

      const roleLabel = this.group.membership?.role
        ? `You: ${this.group.membership.role}`
        : null;
      syncGroupContextNav({
        groupId: this.groupId,
        groupName: this.group.name,
        roleLabel,
        threads: this.activeThreads()
      });
      await this.applyImageProxies();
    } catch (e) {
      this.error = e.message || 'Could not load group';
      logger.warn('GroupShell refresh failed', e);
      teardownGroupContextNav();
    }
    this.render();
    this.bindEvents();
    this.syncChildComponents();
  }

  async applyImageProxies() {
    const uid = this.currentUser?.id;
    if (!uid || !this.group) return;
    if (this.group.header_image_url) {
      this.group._headerProxy = await getProxyUrl(this.group.header_image_url, uid, null);
    } else {
      this.group._headerProxy = null;
    }
    if (this.group.avatar_image_url) {
      this.group._avatarProxy = await getProxyUrl(this.group.avatar_image_url, uid, null);
    } else {
      this.group._avatarProxy = null;
    }
  }

  syncChildComponents() {
    const writer = this.querySelector('post-writer');
    if (writer) {
      if (typeof writer.setGroupThreads === 'function') {
        writer.setGroupThreads(this.activeThreads());
      }
    }
    const feed = this.querySelector('posts-feed');
    if (feed) {
      if (typeof feed.setCurrentUser === 'function') {
        feed.setCurrentUser(this.currentUser);
      }
      const feedGroupId = feed.getAttribute('group-id');
      const feedThreadId = feed.getAttribute('thread-id') || '';
      const shellThreadId = this.threadId !== null && this.threadId !== undefined ? String(this.threadId) : '';
      const groupUnchanged = feedGroupId === String(this.groupId);
      const threadUnchanged = feedThreadId === shellThreadId;
      const shouldRefresh = !feed.hasLoadedOnce || !groupUnchanged || !threadUnchanged;
      if (typeof feed.refresh === 'function' && shouldRefresh) {
        feed.refresh();
      }
    }
  }

  bindEvents() {
    this.querySelector('#addThreadBtn')?.addEventListener('click', () => {
      const modal = this.querySelector('group-add-thread-modal');
      modal?.open(this.groupId);
    });

    this.querySelector('#groupInviteBtn')?.addEventListener('click', () => {
      const modal = this.querySelector('group-invite-modal');
      modal?.open({
        groupId: this.groupId,
        groupName: this.group?.name
      });
    });

    this.querySelector('#groupSettingsBtn')?.addEventListener('click', () => {
      const route = `/groups/${this.groupId}/settings`;
      if (window.router?.navigate) {
        window.router.navigate(route);
      } else {
        window.location.href = route;
      }
    });

    this.querySelector('#createGroupEventBtn')?.addEventListener('click', () => {
      const route = `/events/new?groupId=${this.groupId}`;
      if (window.router?.navigate) {
        window.router.navigate(route);
      } else {
        window.location.href = route;
      }
    });
  }

  escapeHtml(s) {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  renderToolbar() {
    const membership = this.group?.membership;
    const isActiveMember = membership?.status === 'active';
    const showInvite = isActiveMember && canInviteMembers(this.group, membership);
    const inviteBtn = showInvite
      ? '<button type="button" slot="overflow" class="btn btn-secondary" id="groupInviteBtn">🤝 Invite</button>'
      : '';
    const modActions = this.isMod()
      ? `<button type="button" slot="overflow" class="btn btn-secondary" id="addThreadBtn">${THREAD_ICON} Add thread</button>`
      : '';
    const settingsBtn = isActiveMember
      ? '<button type="button" slot="overflow" class="btn btn-secondary" id="groupSettingsBtn">⚙️ Settings</button>'
      : '';
    const showCreateEvent = canCreateGroupEvents(this.group, membership);
    const createEventBtn = showCreateEvent
      ? '<button type="button" slot="primary" class="btn btn-primary" id="createGroupEventBtn">📅 Create event</button>'
      : '';

    return `
      <toolbar-action-bar label="Group actions">
        <toolbar-overflow-actions slot="actions">
          ${createEventBtn}
          ${inviteBtn}
          ${modActions}
          ${settingsBtn}
        </toolbar-overflow-actions>
      </toolbar-action-bar>
    `;
  }

  render() {
    if (Number.isNaN(this.groupId)) {
      this.innerHTML = '<p class="error">Invalid group.</p>';
      return;
    }
    if (this.error && !this.group) {
      this.innerHTML = `<p class="error">${this.escapeHtml(this.error)}</p>`;
      return;
    }
    if (!this.group) {
      this.innerHTML = '<div class="loading-state"><div class="spinner"></div><div>Loading group…</div></div>';
      return;
    }

    const suspended = this.group.membership?.status === 'suspended';
    const isActiveMember = this.group.membership?.status === 'active';
    const isParticipatingMember = ['active', 'limited'].includes(this.group.membership?.status);
    const bannerColor = this.group.header_banner_color || '';
    const bannerClass = ['group-shell-banner'];
    if (this.group._headerProxy) {
      bannerClass.push('has-image');
    }

    const avatarHtml = this.group._avatarProxy
      ? `<img class="group-shell-avatar" src="${this.group._avatarProxy}" alt="" />`
      : '<div class="group-shell-avatar" aria-hidden="true">🌱</div>';

    const threadAttr = this.threadId ? ` thread-id="${this.threadId}"` : '';
    const initialPage = this.getAttribute('initial-page');
    const feedPageAttr = initialPage && parseInt(initialPage, 10) > 1
      ? ` data-initial-page="${initialPage}"`
      : '';
    const currentThread = this.threadId
      ? this.activeThreads().find((t) => t.id === this.threadId)
      : null;
    const threadNameAttr = currentThread
      ? ` thread-name="${this.escapeHtml(currentThread.name)}"`
      : '';
    const showEventsSection = !suspended && !this.threadId && isParticipatingMember;
    const eventsSection = showEventsSection
      ? `<group-events-section group-id="${this.groupId}"></group-events-section>`
      : '';
    const composerAndFeed = suspended
      ? `
        <section class="group-shell-suspended card">
          <h3>Access blocked</h3>
          <p>You are suspended from this group.</p>
        </section>
      `
      : `
        <post-writer group-id="${this.groupId}"${threadAttr}${threadNameAttr}></post-writer>
        <posts-feed group-id="${this.groupId}"${threadAttr}${feedPageAttr}></posts-feed>
      `;

    this.innerHTML = `
      <div class="group-shell">
        <header class="group-shell-header">
          <div class="${bannerClass.join(' ')}"></div>
          <div class="group-shell-heading">
            ${avatarHtml}
            <div class="group-shell-title-block">
              <h1>${this.escapeHtml(this.group.name)}</h1>
              <p>${this.escapeHtml(this.group.description || '')}</p>
            </div>
          </div>
        </header>

        ${this.renderToolbar()}

        ${this.error ? `<p class="error">${this.escapeHtml(this.error)}</p>` : ''}
        ${eventsSection}
        ${composerAndFeed}

        <group-add-thread-modal></group-add-thread-modal>
        <group-invite-modal></group-invite-modal>
      </div>
    `;

    const root = this.querySelector('.group-shell');
    if (root && bannerColor.startsWith('#')) {
      root.style.setProperty('--group-header-color', bannerColor);
    }
    const ban = this.querySelector('.group-shell-banner.has-image');
    if (ban && this.group._headerProxy) {
      ban.style.backgroundImage = `linear-gradient(120deg, rgba(0,0,0,.35), transparent), url(${this.group._headerProxy})`;
    }
  }
}

customElements.define('group-shell', GroupShell);
export default GroupShell;
